import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Config } from "../../../types/config/config";
import { Listing } from "../../../types/listing/listing";
import { publishListing } from "../../../redux/actions/listingEntry/publish-listing";
import { setReloadListingId } from "../../../redux/actions/system/set-reload-listing-id";
import { setAlertMessage } from "../../../redux/actions/system/set-alert-message";
import { AlertMessagingType } from "../../../types/state";
import {
  applyMiqDiffChanges,
  fetchMiqSyncData,
  MiqDiscardedFields,
  saveDiscardedFields,
} from "../../../api/miq/miq-sync";
import { MiqDeltaInput } from "../../../types/miq/miq-diff";
import {
  mapMiqDiffs,
  MiqFieldUpdate,
  MiqListingUpdates,
} from "../../../utils/miq/miq-sync-mapper";
import { isMiqSyncEnabled } from "../../../utils/config/miq-sync-enabled";

const isMiqSyncPending = (listing: Listing): boolean => {
  return (
    listing.miqSyncFlag === true ||
    String(listing.miqSyncFlag).toLowerCase() === "true" ||
    listing.miqSyncFlag === (1 as any)
  );
};


const hasBeenPublished = (listing: Listing): boolean =>
  listing.published === true || Boolean(listing.datePublished);


const hasNewMiqUpdates = (listingUpdates: MiqListingUpdates): boolean =>
  listingUpdates.updates.some((update) => update.status !== "discarded");

interface UseMiqSyncResult {
  miqUpdates: MiqListingUpdates | null;
  isApplyingMiqUpdates: boolean;
  isIgnoringMiqUpdates: boolean;
  applyMiqUpdates: (selectedUpdates: MiqFieldUpdate[]) => void;
  ignoreMiqUpdates: () => void;
}

// Owns the MIQ diff/discard fetch-and-review lifecycle for the currently loaded listing (route `id`).
export const useMiqSync = (
  id: string,
  listing: Listing,
  config: Config,
): UseMiqSyncResult => {
  const dispatch = useDispatch();
  const miqSyncEnabled = isMiqSyncEnabled(config);
  const [miqUpdates, setMiqUpdates] = useState<MiqListingUpdates | null>(null);
  const [isApplyingMiqUpdates, setIsApplyingMiqUpdates] = useState<boolean>(
    false,
  );
  const [isIgnoringMiqUpdates, setIsIgnoringMiqUpdates] = useState<boolean>(
    false,
  );
  // tracks which loaded listing id has already started an MIQ diff request
  const miqDiffFetchedForId = useRef<string | null>(null);
  const prefetchedMiqSync = useRef<{
    id: string;
    deltas: MiqDeltaInput[];
    discardedFields: MiqDiscardedFields;
  } | null>(null);
  const [prefetchVersion, setPrefetchVersion] = useState(0);
  // guards state updates from in-flight async work (e.g. publish redirect) after this hook's owner has unmounted
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  // Start the MIQ request from the route id while the full listing is loading.
  useEffect(() => {
    let cancelled = false;
    if (!miqSyncEnabled) {
      miqDiffFetchedForId.current = null;
      prefetchedMiqSync.current = null;
      setMiqUpdates(null);
      return;
    }
    if (!id || miqDiffFetchedForId.current === id) {
      return;
    }

    miqDiffFetchedForId.current = id;
    fetchMiqSyncData(Number(id))
      .then(({ deltas, discardedFields }) => {
        if (cancelled) {
          return;
        }
        if (!deltas) {
          miqDiffFetchedForId.current = null;
          return;
        }
        prefetchedMiqSync.current = { id, deltas, discardedFields };
        if (isMountedRef.current) {
          setPrefetchVersion((version) => version + 1);
        }
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        miqDiffFetchedForId.current = null;
        prefetchedMiqSync.current = null;
        if (isMountedRef.current) {
          setPrefetchVersion((version) => version + 1);
        }
        console.error("Unable to prefetch MIQ updates", error);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, miqSyncEnabled]);

  useEffect(() => {
    let cancelled = false;
    const listingId = listing && listing.id ? Number(listing.id) : 0;
    const listingIdKey = listingId > 0 ? String(listingId) : null;

    if (
      !miqSyncEnabled ||
      !listingIdKey ||
      listingIdKey !== id ||
      !isMiqSyncPending(listing) ||
      !hasBeenPublished(listing)
    ) {
      setMiqUpdates(null);
      return () => {
        cancelled = true;
      };
    }

    if (
      prefetchedMiqSync.current &&
      prefetchedMiqSync.current.id === listingIdKey
    ) {
      const { deltas, discardedFields } = prefetchedMiqSync.current;
      const listingUpdates = mapMiqDiffs(
        listing,
        config,
        deltas,
        discardedFields,
      );
      if (!cancelled) {
        setMiqUpdates(
          hasNewMiqUpdates(listingUpdates) ? listingUpdates : null,
        );
      }
      return () => {
        cancelled = true;
      };
    }

    // already fetched (or in flight) for this listing - avoid re-fetching after unrelated listing
    // updates (e.g. after applying MIQ updates, which doesn't change the flag), which would reopen the popup
    if (miqDiffFetchedForId.current === listingIdKey) {
      return () => {
        cancelled = true;
      };
    }
    miqDiffFetchedForId.current = listingIdKey;

    fetchMiqSyncData(listingId)
      .then(({ deltas, discardedFields }) => {
        if (!cancelled && deltas) {
          const listingUpdates = mapMiqDiffs(
            listing,
            config,
            deltas,
            discardedFields,
          );
          setMiqUpdates(
            hasNewMiqUpdates(listingUpdates) ? listingUpdates : null,
          );
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Unable to load MIQ updates", error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [listing && listing.id, listing && listing.miqSyncFlag, prefetchVersion, miqSyncEnabled]);

  const applyMiqUpdates = (selectedUpdates: MiqFieldUpdate[]) => {
    if (!miqSyncEnabled || !miqUpdates || isApplyingMiqUpdates || isIgnoringMiqUpdates) {
      return;
    }

    setIsApplyingMiqUpdates(true);

    const deltas: MiqDeltaInput[] = selectedUpdates
      .map((update) => update.delta)
      .filter(
        (delta) =>
          delta &&
          typeof delta.jsonPath === "string" &&
          delta.jsonPath.trim().length > 0,
      );

    const selectedFields = selectedUpdates.map((update) => update.field);


    const newlyDiscardedUpdates = miqUpdates.updates.filter(
      (update) =>
        update.status === "new" && selectedFields.indexOf(update.field) === -1,
    );

 
    const consideredFieldPaths = miqUpdates.updates
      .filter(
        (update) =>
          update.status === "new" || selectedFields.indexOf(update.field) !== -1,
      )
      .map((update) => update.delta && update.delta.jsonPath)
      .filter(
        (jsonPath): jsonPath is string =>
          typeof jsonPath === "string" && jsonPath.trim().length > 0,
      );

    applyMiqDiffChanges(Number(miqUpdates.listingId), deltas)
      .then(() => {       
        return saveDiscardedFields(
          miqUpdates.listingId,
          newlyDiscardedUpdates,
          consideredFieldPaths,
        );
      })
      .then(() => {
        prefetchedMiqSync.current = null;
        // Keep a failed publish from immediately reopening the same MIQ review.
        miqDiffFetchedForId.current = miqUpdates.listingId;

        const autoPublish =
          config &&
          config.featureFlags &&
          (config.featureFlags.miqApplyAndAutoPublish === true ||
            String(config.featureFlags.miqApplyAndAutoPublish).toLowerCase() ===
              "true");

        // Region-configurable: publish the listing immediately after applying MIQ updates,
        // reusing the same publish flow as the manual Publish/Republish button. Keep the MIQ
        // popup open (still in its "applying" state) until publish finishes, so the flow goes
        // straight into publishing instead of flashing the plain edit page first.
        const publishPromise = autoPublish
          ? ((dispatch(
              publishListing({
                ...listing,
                id: Number(miqUpdates.listingId),
              }),
            ) as unknown) as Promise<void>)
          : Promise.resolve();

        return publishPromise.then(() => {
          if (isMountedRef.current) {
            setMiqUpdates(null);
          }

          // applyMiqDiff already saved the listing server-side, but the currently loaded
          // listing in Redux is stale - reload it so the applied changes show up in the UI.
          // Skipped when auto-publishing since that flow navigates away from this page.
          if (!autoPublish) {
            dispatch(setReloadListingId(Number(miqUpdates.listingId)));
          }
        });
      })
      .catch((error) => {
        console.error("Unable to apply MIQ updates", error);

        dispatch(
          setAlertMessage({
            show: true,
            message:
              error && error.message
                ? `There was an error applying the MIQ updates: ${error.message}`
                : "There was an error applying the MIQ updates.",
            type: AlertMessagingType.ERROR,
            allowClose: true,
          }),
        );
      })
      .finally(() => {
        if (isMountedRef.current) {
          setIsApplyingMiqUpdates(false);
        }
      });
  };

  const ignoreMiqUpdates = () => {
    if (!miqSyncEnabled || !miqUpdates || isApplyingMiqUpdates || isIgnoringMiqUpdates) {
      return;
    }

    setIsIgnoringMiqUpdates(true);

    // Fields already discarded from an earlier round are already ignored - only fields newly
    // reported as differing this round need to be sent, so their rows/timestamps are left alone.
    const newFieldUpdates = miqUpdates.updates.filter(
      (update) => update.status === "new",
    );

    const consideredFieldPaths = newFieldUpdates
      .map((update) => update.delta && update.delta.jsonPath)
      .filter(
        (jsonPath): jsonPath is string =>
          typeof jsonPath === "string" && jsonPath.trim().length > 0,
      );

    saveDiscardedFields(
      miqUpdates.listingId,
      newFieldUpdates,
      consideredFieldPaths,
    )
      .then(() => {
        prefetchedMiqSync.current = null;

        if (isMountedRef.current) {
          setMiqUpdates(null);
        }
      })
      .catch((error) => {
        console.error("Unable to save discarded MIQ updates", error);

        dispatch(
          setAlertMessage({
            show: true,
            message:
              error && error.message
                ? `There was an error saving the discarded MIQ updates: ${error.message}`
                : "There was an error saving the discarded MIQ updates.",
            type: AlertMessagingType.ERROR,
            allowClose: true,
          }),
        );
      })
      .finally(() => {
        if (isMountedRef.current) {
          setIsIgnoringMiqUpdates(false);
        }
      });
  };

  return {
    miqUpdates,
    isApplyingMiqUpdates,
    isIgnoringMiqUpdates,
    applyMiqUpdates,
    ignoreMiqUpdates,
  };
};
